import React, { useRef, useState, useEffect } from 'react';
import { View, StyleSheet, FlatList } from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { AnchoredDropdownItem } from '@/components/common/admin/desktop/dropdown/AnchoredDropdownItem';
import { useIsFocused, useRoute, RouteProp } from '@react-navigation/native';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { useResidentConversation } from '@/hooks/residents/useMessages';
import { useCombinedConversations, UnifiedConversation } from '@/hooks/residents/useCombinedConversations';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components/common/residents/desktop/Button';
import { AnchoredDropdown as RealAnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import ResidentCreateConversation from '@/components/residents/propertyChat/ResidentCreateConversation';
import ResidentChatWindow from '@/components/residents/propertyChat/ResidentChatWindow';
import { EmptyMessageWindow } from '@/components/residents/propertyChat/EmptyMessageWindow';
import { AvatarCircle } from '@/components/common/AvatarCircle';
import { MESSAGE_SORT_OPTIONS, getSortOptionLabel, type MessageSortOption } from '@/constants/residents/messages';
import { MessagesStackNavigator } from '@/navigation/residents/mobile/MessagesStackNavigator';

const SearchIcon: React.FC<{ size?: number; color?: string }> = React.memo(({ size = 18, color = '#777' }) => {
  const circle = Math.floor(size * 0.6);
  const border = Math.max(2, Math.floor(size * 0.12));
  const handleW = Math.max(2, Math.floor(size * 0.12));
  const handleH = Math.floor(size * 0.32);
  return (
    <View style={[searchIconStyles.container, { width: size, height: size }]}>
      <View style={{
        width: circle, height: circle,
        borderRadius: circle / 2, borderWidth: border, borderColor: color,
      }} />
      <View style={[searchIconStyles.handle, {
        width: handleW, height: handleH,
        backgroundColor: color, borderRadius: handleW / 2,
      }]} />
    </View>
  );
});

const searchIconStyles = StyleSheet.create({
  container: { alignItems: 'center', justifyContent: 'center' },
  handle: { position: 'absolute', bottom: 0, right: 0, transform: [{ rotate: '-45deg' }] },
});

export const MessagesScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  if (!isDesktop) {
    return <MessagesStackNavigator />;
  }

  return <DesktopMessagesView />;
};

const DesktopMessagesView: React.FC = () => {
  const { colors } = useThemeColors();
  const styles = useMessagesStyles();
  const searchHover = useHoverStyle('field');
  const isFocused = useIsFocused();
  const route = useRoute<RouteProp<DesktopMainParamList, 'Messages'>>();
  const initialSubject = route.params?.initialSubject;
  const existingConversationId = route.params?.existingConversationId;
  
  const [selectedConversationId, setSelectedConversationId] = useState<number | undefined>(undefined);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingSubject, setPendingSubject] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (existingConversationId) {
      setSelectedConversationId(existingConversationId);
      setShowCreate(false);
      setPendingSubject(undefined);
    } else if (initialSubject) {
      setPendingSubject(initialSubject);
      setShowCreate(true);
      setSelectedConversationId(undefined);
    }
  }, [initialSubject, existingConversationId]);

  const {
    conversations,
    isLoading,
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
  } = useCombinedConversations();

  const { conversation: propertyConversation } = useResidentConversation(selectedConversationId, { 
    skip: !isFocused 
  });

  const sortButtonRef = useRef<View | null>(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  const openSort = () => {
    if (sortButtonRef.current) {
      sortButtonRef.current.measureInWindow((x, y, width, height) => {
        setSortAnchor({ x, y, w: width, h: height });
        setIsSortOpen(true);
      });
    }
  };
  const closeSort = () => setIsSortOpen(false);

  const handleConversationPress = (item: UnifiedConversation) => {
    const isCurrentlySelected = selectedConversationId === item.originalId;
    setSelectedConversationId(isCurrentlySelected ? undefined : item.originalId);
  };

  const renderConversationItem = ({ item }: { item: UnifiedConversation }) => {
    const isSelected = selectedConversationId === item.originalId;
    
    return (
      <Hoverable
        hover={isSelected ? 'none' : 'row'}
        onPress={() => handleConversationPress(item)}
        style={[styles.conversationItem, isSelected && styles.conversationItemSelected]}
      >
        <View style={styles.conversationAvatarWrap}>
          {item.isGroup ? (
            <RESIDENTS_SVG_COMPONENTS.GROUP width={20} height={20} color={colors.text.primary} />
          ) : (
            <AvatarCircle
              firstName=""
              lastName=""
              size={32}
              placeholderColor={PRIMITIVE_COLORS.resioBlue}
            />
          )}
        </View>
        <View style={styles.conversationTextWrap}>
          <Text style={styles.conversationSubject} numberOfLines={2}>{item.title}</Text>
          <Text style={styles.conversationPreview} numberOfLines={1}>{item.preview}</Text>
          <Text style={styles.conversationMeta} numberOfLines={1}>
            {item.recipientCount 
              ? `${item.recipientCount} Recipients` 
              : 'Property Chat'}
          </Text>
        </View>
        {item.hasUnread && <View style={styles.unreadDot} />}
      </Hoverable>
    );
  };

  return (
    <View style={styles.root}>
      <View style={styles.row}> 
        <View style={styles.leftPane}>
          <View style={styles.leftHeader}> 
            <View style={[styles.searchContainer, searchHover.viewStyle]} {...(searchHover.mouseProps as any)}>
              <SearchIcon size={18} color={colors.text.subtle} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search"
                placeholderTextColor={colors.text.subtle}
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
            </View>
            <RNButton title="New Message" variant="primary" size="md" style={styles.newMessageBtn} onPress={() => setShowCreate(true)} />
          </View>
          <View style={styles.sortRow}> 
            <View style={styles.sortWrap} ref={sortButtonRef}>
              <Hoverable hover="glass" style={styles.sortToggle} onPress={openSort}>
                <Text style={styles.sortToggleText}>{getSortOptionLabel(sortOption as MessageSortOption)}</Text>
              </Hoverable>
            </View>
          </View>
          <FlatList
            data={conversations}
            keyExtractor={(item: UnifiedConversation) => item.id}
            renderItem={renderConversationItem}
            style={styles.list}
            contentContainerStyle={styles.listContent}
          />
        </View>
        <View style={styles.rightPane}>
          {showCreate ? (
            <ResidentCreateConversation 
              onClose={() => {
                setShowCreate(false);
                setPendingSubject(undefined);
              }}
              initialSubject={pendingSubject}
            />
          ) : !selectedConversationId ? (
            <EmptyMessageWindow />
          ) : propertyConversation ? (
            <View style={styles.chatWrap}>
              <View style={styles.chatHeader}>
                <View style={styles.chatHeaderText}>
                  <Text style={styles.chatHeaderTitle} numberOfLines={2}>{propertyConversation.subject}</Text>
                  <Text style={styles.chatHeaderSubtitle} numberOfLines={1}>{(() => {
                    const people = (propertyConversation.accounts || []).filter((a: any) => a.ConversationAccount?.type === 'resident');
                    if (people.length === 0) return 'No recipients';
                    if (people.length === 1) {
                      const p = people[0];
                      return p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.email;
                    }
                    return `${people.length} Recipients`;
                  })()}</Text>
                  <Text style={styles.chatHeaderMeta} numberOfLines={1}>{(() => {
                    const d = new Date(propertyConversation.createdAt);
                    const formattedDate = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    const formattedTime = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true, timeZoneName: 'short' });
                    return `Created ${formattedDate} ${formattedTime}`;
                  })()}</Text>
                </View>
              </View>
              <View style={styles.chatBody}>
                <ResidentChatWindow conversationId={selectedConversationId} />
              </View>
            </View>
          ) : (
            <EmptyMessageWindow />
          )}
        </View>
      </View>

      <RealAnchoredDropdown
        isOpen={isSortOpen}
        anchor={sortAnchor as any}
        width={(sortAnchor?.w || 280)}
        maxHeight={360}
        onRequestClose={closeSort}
      >
        <View>
          {MESSAGE_SORT_OPTIONS.map((option) => (
            <AnchoredDropdownItem
              key={option.key}
              selected={sortOption === option.key}
              style={[
                styles.sortMenuItem,
                sortOption === option.key && styles.sortMenuItemSelected,
              ]}
              onPress={() => {
                setSortOption(option.key as any);
                closeSort();
              }}
            >
              <Text style={[
                styles.sortMenuItemText,
                sortOption === option.key && styles.sortMenuItemTextSelected,
              ]}>{option.label}</Text>
            </AnchoredDropdownItem>
          ))}
        </View>
      </RealAnchoredDropdown>
    </View>
  );
};

const useMessagesStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      root: {
        flex: 1,
        padding: 12,
        backgroundColor: 'transparent',
      },
      row: {
        flex: 1,
        flexDirection: 'row',
        gap: 12,
        minHeight: 360,
        overflow: 'hidden',
      },
      leftPane: {
        width: '35%',
        alignSelf: 'stretch',
        backgroundColor: colors.card.bg,
        borderWidth: 1,
        borderColor: colors.card.border,
        borderRadius: 16,
        overflow: 'hidden',
      },
      rightPane: {
        flex: 1,
        alignSelf: 'stretch',
        backgroundColor: colors.card.bg,
        borderWidth: 1,
        borderColor: colors.card.border,
        borderRadius: 16,
        overflow: 'hidden',
      },
      leftHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 12,
        gap: 8,
        borderBottomWidth: 1,
        borderBottomColor: colors.separator,
      },
      searchContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.glass.buttonBg,
        borderWidth: 1,
        borderColor: colors.glass.buttonBorder,
        borderRadius: 10,
        minHeight: 40,
        paddingHorizontal: 12,
        gap: 8,
      },
      searchInput: {
        flex: 1,
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.primary,
        padding: 0,
      },
      newMessageBtn: {
        minHeight: 40,
      },
      sortRow: {
        padding: 8,
        paddingHorizontal: 12,
      },
      sortWrap: {
        position: 'relative',
        flex: 1,
      },
      sortToggle: {
        borderRadius: 10,
        backgroundColor: colors.glass.buttonBg,
        borderWidth: 1,
        borderColor: colors.glass.buttonBorder,
        paddingHorizontal: 12,
        paddingVertical: 10,
      },
      sortToggleText: {
        ...REDESIGN_TYPOGRAPHY.body,
        fontWeight: '500',
        color: colors.text.subtle,
      },
      sortMenuItem: {
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.separator,
        backgroundColor: colors.card.bg,
      },
      sortMenuItemSelected: {
        backgroundColor: colors.accent.blueBgLight,
      },
      sortMenuItemText: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.secondary,
      },
      sortMenuItemTextSelected: {
        color: PRIMITIVE_COLORS.resioBlue,
        fontWeight: '600',
      },
      list: { flex: 1 },
      listContent: { paddingBottom: 16 },
      conversationItem: {
        flexDirection: 'row',
        padding: 12,
        paddingHorizontal: 16,
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: colors.separator,
      },
      conversationItemSelected: {
        backgroundColor: colors.accent.blueBgLight,
      },
      conversationAvatarWrap: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.activityIcon.bg,
        borderWidth: 1,
        borderColor: colors.activityIcon.border,
        alignItems: 'center',
        justifyContent: 'center',
      },
      unreadDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: PRIMITIVE_COLORS.warningOrange,
        marginLeft: 8,
      },
      conversationTextWrap: { marginLeft: 12, flex: 1 },
      conversationSubject: {
        ...REDESIGN_TYPOGRAPHY.cardTitle,
        color: colors.text.primary,
      },
      conversationPreview: {
        ...REDESIGN_TYPOGRAPHY.small,
        color: colors.text.muted,
        marginTop: 2,
      },
      conversationMeta: {
        ...REDESIGN_TYPOGRAPHY.caption,
        color: colors.text.subtle,
        marginTop: 2,
      },
      chatWrap: { flex: 1 },
      chatHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.separator,
      },
      chatHeaderText: { flex: 1, marginRight: 12 },
      chatHeaderTitle: {
        ...REDESIGN_TYPOGRAPHY.sectionTitle,
        color: colors.text.primary,
        marginBottom: 2,
      },
      chatHeaderSubtitle: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.muted,
      },
      chatHeaderMeta: {
        ...REDESIGN_TYPOGRAPHY.caption,
        color: colors.text.dim,
        marginTop: 2,
      },
      chatBody: { flex: 1 },
    }),
  );
